import {
  hasLoadedAlignmentInProject,
  useApiServices,
  useDataContext,
  useCapabilityRegistryContext,
  useThemeContext,
  useProjectSettings,
} from '@mobile-mapping/providers';
import { fetchGeoDataFilesByCsib } from '@data-access/coordinates';
import {
  FeatureToggleCapabilityCoordinator,
  PaneContents,
  PaneVisibilityCoordinator,
  TileMapCoordinatesCapabilityCoordinator,
  ViewMode,
  ViewModeCapabilityCoordinator,
  viewModeEquals,
  resolveSyncCoordinator,
} from '@mobile-mapping/web3d';
import { Web3DViewer } from '@technology/web3d';
import { useKeyDownEvent } from '@solid-primitives/keyboard';
import { useKeyboardStationNavigation } from './hooks/useKeyboardStationNavigation';
import { PaneElementProps } from '@trimble/pane-layout';
import { PaneSyncer } from '@trimble/pane-sync';
import { useHover, useLogger } from '@trimble/solidjs-utils';
import {
  AlignmentNavigationToolbar,
  BasicButton,
  CameraControlSelector,
  TrbToolbar,
  ToolbarPositioner,
  Anchor,
} from '@trimble/ui-components/solidjs';
import { LengthUnit } from '@fulcrum/lib';
import { PaneToolbarVisibilityControl } from './components/PaneToolbarVisibilityControl';
import { ObjectSelectionToolbarContainer } from './components/ObjectSelectionToolbarContainer';
import * as ViewerFactory from '@trimble/utilities';
import { ThreeJsManager } from '@trimble/utilities';
import {
  MutuallyExclusiveCapabilityCoordinator,
  NavigationCapabilityCoordinator,
  CapabilitiesCollection,
  Hide2DMarkupCapabilityCoordinator,
} from '@trimble/web3d';
import {
  AlignmentNavigationCapability,
  FreeViewCapability,
  ImageCapabilityBase,
  ImagePointcloudCapability,
  MarkupCapability,
  SelectionActionsCapability,
  StationsNavigationCapability,
} from '@trimble/web3d-capabilities';
import {
  MarkupsCapabilityProvider,
  PointCloudCapabilityProvider,
  PointCloudStylingCapabilityProvider,
  StationsCapabilityProvider,
  TrimBimCapabilityProvider,
  TrajectoryMetadataProvider,
  ViewsCapabilityProvider,
} from '@trimble/web3d-providers';
import {
  createEffect,
  createMemo,
  createSignal,
  JSX,
  on,
  onCleanup,
  onMount,
  Show,
} from 'solid-js';

import { createRotatePanoramasAction } from './station-load/rotate-panoramas-action';
import { addTilesetPrefetchEffect } from './station-load/tileset-prefetch';
import { useStationLoadContext } from './station-load/station-load-context';

import {
  EmptyViewerState,
  EmptyViewerStateProps,
} from './components/EmptyViewerState';
import { PaneBottomBar } from './components/PaneBottomBar';
import { applyViewerPreferences } from './apply-viewer-preferences';
import { asError } from '@trimble/logging';
import { t } from '@trimble/i18n';

export const PaneViewer = (
  props: PaneElementProps<PaneContents, ViewMode>
): JSX.Element => {
  let navCapCoord: NavigationCapabilityCoordinator | undefined;

  const logger = useLogger().getLogger('PaneViewer');

  const [viewModeCapCoord, setViewModeCapCoord] =
    createSignal<ViewModeCapabilityCoordinator>();

  const [emptyViewerStateProps, setEmptyViewerStateProps] =
    createSignal<EmptyViewerStateProps>();

  let paneSyncer: PaneSyncer | undefined;
  let unregisterPaneSyncer: (() => void) | undefined;

  const viewModeFromLayout = createMemo(
    (prev: ViewMode | undefined): ViewMode => {
      const activeLayout = props.layoutConfig.layouts.layouts.find(
        (l) => l.label === props.layoutConfig.layouts.activeLayoutLabel
      );
      if (activeLayout === undefined) {
        throw new Error('Active layout must be selected');
      }

      const activeRow = activeLayout.paneRows[props.layoutConfig.rowIdx];
      const activeCell = activeRow.cells[props.layoutConfig.cellIdx];

      const next: ViewMode =
        activeCell.contentConfig.type !== 'Image'
          ? {
              type: activeCell.contentConfig.type,
            }
          : {
              type: activeCell.contentConfig.type,
              cameraIndex: activeCell.contentConfig.cameraIndex,
            };

      if (!prev) return next;
      return viewModeEquals(prev, next) ? prev : next;
    }
  );

  createEffect(
    on(
      () => props.layoutConfig.layouts.activeLayoutLabel,
      () => {
        const vm = viewModeFromLayout();
        const coord = viewModeCapCoord();
        if (coord) {
          coord.setViewMode(vm);
        }
      },
      { defer: true }
    )
  );

  createEffect(
    on(
      () => viewModeCapCoord()?.viewMode(),
      (vm) => {
        if (!vm) return;

        const activeLabel = props.layoutConfig.layouts.activeLayoutLabel;

        props.layoutConfig.setLayouts(
          'layouts',
          (layouts) => layouts.label === activeLabel,
          'paneRows',
          props.layoutConfig.rowIdx,
          'cells',
          props.layoutConfig.cellIdx,
          'contentConfig',
          vm
        );
      }
    )
  );

  const { web3dSettings, preferenceSettings } = useThemeContext();
  const {
    dataLoadingService,
    dataState,
    currentLoadedCsib,
    currentLoadedCenter,
  } = useDataContext();
  const { formattingSettings } = useProjectSettings();
  const { tilesetPrefetchContext } = useStationLoadContext();

  let placeholder!: HTMLDivElement;
  let viewerWrapper:
    | ReturnType<typeof ViewerFactory.create3DViewer>
    | undefined;

  const { imagingApi, coordinatesClient } = useApiServices();

  const [viewer, setViewer] = createSignal<Web3DViewer>();

  const viewModeType = createMemo(() => viewModeCapCoord()?.viewMode().type);

  const allowMarkups = createMemo(() => {
    return viewModeType() !== '2D';
  });

  const capabilityRegistry = useCapabilityRegistryContext().capabilityRegistry;
  /* eslint-disable solid/reactivity -- rowIdx/cellIdx are static for the pane's lifetime */
  const capRegContainer = capabilityRegistry.registerPaneContainer(
    undefined,
    props.layoutConfig.rowIdx,
    props.layoutConfig.cellIdx
  );
  /* eslint-enable solid/reactivity */

  const allowMarkupEditing = createMemo(
    () =>
      viewModeType() === '3D' &&
      capRegContainer
        .get(StationsNavigationCapability)()
        ?.selectedStationIndex() == null
  );
  const selectionActionsCapability = ():
    | SelectionActionsCapability
    | undefined => capRegContainer.get(SelectionActionsCapability)();

  const markupCapability = (): MarkupCapability | undefined =>
    capabilityRegistry.get(MarkupCapability)();

  const isInStationNavigation = createMemo(
    () =>
      viewModeCapCoord()
        ?.stationNavigationController()
        ?.selectedStationIndex() != null
  );

  // The top floating toolbar hosts the per-pane visibility dropdown plus
  // the 3D-only camera controls. It stays visible
  // in every view mode so the visibility dropdown is always reachable — in
  // 2D map mode the dropdown surfaces the frustum / stationMarkers /
  // navigationPath toggles (see `buildRowsForViewMode`), while the 3D-only
  // controls inside are gated separately (see `viewModeType() === '3D'`
  // below) so they don't clutter 2D / panorama / image panes, where there's
  // no camera-mode switching.
  const isFloatingToolbarVisible = createMemo(() => viewModeType() != null);

  // Show station-nav whenever its controller is ready *and* the pane is
  // currently in an image view, or it's a 3D pane that's mid-station-
  // navigation (the close button leaves station-nav mode in 3D only).
  const isStationNavBarVisible = createMemo(() => {
    if (!viewModeCapCoord()?.stationNavigationController()) return false;
    return (
      viewModeType() === 'Image' ||
      viewModeType() === 'Panorama' ||
      capRegContainer
        .get(StationsNavigationCapability)()
        ?.selectedStationIndex() != null
    );
  });

  const viewToolActions = createRotatePanoramasAction({
    viewModeType,
    planarContents: () => props.data.planarContents(),
  });

  createEffect(
    on(allowMarkupEditing, (allowed) => {
      if (allowed) {
        return;
      }
      const mc = markupCapability();
      if (!mc?.isActive()) {
        return;
      }
      capabilityRegistry
        .get(MarkupsCapabilityProvider)()
        ?.discardPendingMarkups();
      mc.deactivate();
    })
  );

  const isAlignmentMeasurementInteractionAllowed = createMemo(() => {
    if (viewModeType() !== '3D') {
      return false;
    }
    return hasLoadedAlignmentInProject(dataState);
  });

  const isImageLoading = createMemo(() => {
    const vm = viewModeCapCoord()?.viewMode();
    if (vm?.type !== 'Image' && vm?.type !== 'Panorama') return false;

    const imageCaps = capRegContainer.get(
      CapabilitiesCollection<ImageCapabilityBase>
    )();
    if (!imageCaps) return false;

    const cameraIndex = vm.type === 'Image' ? vm.cameraIndex : 0;
    return imageCaps.capabilities[cameraIndex]?.isImageLoading() ?? false;
  });

  const { isHovered, ref: hoverRef } = useHover();
  const keyDownEvent = useKeyDownEvent();

  // Component-scope + keyed on the viewer signal so it re-runs for every pane when
  // the preferences change.
  createEffect(() => {
    const activeViewer = viewer();
    if (!activeViewer) return;

    applyViewerPreferences(activeViewer.settings, preferenceSettings());
  });

  onMount(() => {
    viewerWrapper = ViewerFactory.create3DViewer(placeholder, props.web3DPath, {
      fxaa: undefined,
      msaa: false,
      renderEdges: true,
      ssao: true,
      orderIndependentTransparency: true,
      vertexInterpolationMaterials: false,
      transparencyPeelsCount: 1,
    });
    viewerWrapper.isAlignmentMeasurementInteractionAllowed =
      isAlignmentMeasurementInteractionAllowed;
    setViewer(viewerWrapper.viewer);
    paneSyncer = new PaneSyncer(
      viewerWrapper.viewer,
      capRegContainer,
      resolveSyncCoordinator,
      navCapCoord
    );

    capRegContainer.setViewerWrapper(viewerWrapper);

    const threeJsManager = new ThreeJsManager(
      viewerWrapper.viewer,
      viewerWrapper.threejsPlugin
    );

    const freeViewCap = capRegContainer.register(
      FreeViewCapability,
      new FreeViewCapability(viewerWrapper.viewer)
    );

    capRegContainer.register(
      SelectionActionsCapability,
      new SelectionActionsCapability(viewerWrapper.viewer)
    );

    capRegContainer.register(
      CapabilitiesCollection<ImageCapabilityBase>,
      new CapabilitiesCollection<ImageCapabilityBase>()
    );

    const pcStylingProvider = capRegContainer.get(
      PointCloudStylingCapabilityProvider
    );

    capRegContainer.register(
      PointCloudCapabilityProvider,
      new PointCloudCapabilityProvider(
        viewerWrapper.viewer,
        pcStylingProvider()
      )
    );

    capRegContainer.register(
      TrimBimCapabilityProvider,
      new TrimBimCapabilityProvider(viewerWrapper.viewer)
    );
    capRegContainer.register(
      TrajectoryMetadataProvider,
      new TrajectoryMetadataProvider(viewerWrapper.viewer)
    );

    // Highlight is always on; the color is user-configurable via preferences.
    viewerWrapper.viewer.settings.clippingHighlight = true;

    capRegContainer.register(
      MutuallyExclusiveCapabilityCoordinator,
      new MutuallyExclusiveCapabilityCoordinator(capRegContainer)
    );

    navCapCoord = capRegContainer.register(
      NavigationCapabilityCoordinator,
      new NavigationCapabilityCoordinator(capRegContainer, freeViewCap)
    );

    capRegContainer.register(
      Hide2DMarkupCapabilityCoordinator,
      new Hide2DMarkupCapabilityCoordinator(
        capRegContainer,
        viewerWrapper,
        allowMarkups,
        allowMarkupEditing
      )
    );

    const viewModeCoordinator = capRegContainer.register(
      ViewModeCapabilityCoordinator,
      new ViewModeCapabilityCoordinator(
        capRegContainer,
        viewModeFromLayout(),
        {
          threeJsManager,
          dataLoadingService,
          web3dSettings,
          fetchGeoDataFiles: (csib64) =>
            fetchGeoDataFilesByCsib(coordinatesClient, csib64),
          datasetCsib: currentLoadedCsib,
          datasetCenter: currentLoadedCenter,
          getViewsProvider: () =>
            capabilityRegistry.get(ViewsCapabilityProvider)(),
        },
        viewerWrapper
      )
    );
    setViewModeCapCoord(viewModeCoordinator);

    capRegContainer.register(
      FeatureToggleCapabilityCoordinator,
      new FeatureToggleCapabilityCoordinator(capRegContainer)
    );

    capRegContainer.register(
      PaneVisibilityCoordinator,
      new PaneVisibilityCoordinator(
        () => capabilityRegistry.get(ViewsCapabilityProvider)(),
        () => web3dSettings().imageViewPointcloudClippingDistance
      )
    );

    setEmptyViewerStateProps({
      viewMode: () => viewModeCoordinator.viewMode(),
      cameraIndex: () => viewModeCoordinator.currentCameraIndex(),
      datasetCsib: currentLoadedCsib,
      pcProvider: () => capRegContainer.get(PointCloudCapabilityProvider)(),
      trbProvider: () => capRegContainer.get(TrimBimCapabilityProvider)(),
      imagePointcloudCap: () =>
        capRegContainer.get(ImagePointcloudCapability)(),
      imageCaps: () =>
        capRegContainer.get(CapabilitiesCollection<ImageCapabilityBase>)(),
      stationsProvider: () =>
        capabilityRegistry.get(StationsCapabilityProvider)(),
      backgroundTopColor: () => preferenceSettings().backgroundTopColor,
      backgroundBottomColor: () => preferenceSettings().backgroundBottomColor,
    });

    createEffect(
      on(viewModeCoordinator.contentUpdated, () => {
        paneSyncer?.reset();
      })
    );

    createEffect(
      on(
        () =>
          capRegContainer
            .get(TileMapCoordinatesCapabilityCoordinator)()
            ?.contentUpdated(),
        () => {
          paneSyncer?.reset();
        }
      )
    );

    createEffect(() => {
      const vm = viewModeCoordinator.viewMode();
      if (!viewerWrapper) return;
      const inStationNavigation = isInStationNavigation();
      const isAlignmentNavActive =
        capRegContainer.get(AlignmentNavigationCapability)()?.isActive() ??
        false;

      viewerWrapper.compassPlugin.enabled =
        vm.type === '3D' && !inStationNavigation;

      viewerWrapper.compassPlugin.interactive = !(
        isAlignmentNavActive || inStationNavigation
      );
    });

    addSyncEffect(viewerWrapper.viewer);

    // Keep the active camera image up to date when the selected camera or camera list changes.
    createEffect(
      on(
        [
          () => viewModeCapCoord()?.currentCameraIndex() ?? null,
          () =>
            capRegContainer.get(CapabilitiesCollection<ImageCapabilityBase>)(),
        ],
        ([camInd, imgCaps]) => {
          if (imgCaps == null || camInd == null) {
            return;
          }
          imgCaps.capabilities[camInd]?.activate();
        }
      )
    );

    // Cleanup images.
    createEffect(
      on(
        () =>
          capRegContainer.get(CapabilitiesCollection<ImageCapabilityBase>)()
            ?.capabilities ?? [],
        (imgCaps, prevImageCaps) => {
          prevImageCaps
            ?.filter((p) => !imgCaps.some((c) => c == p))
            .forEach((p) => void p.cleanup());
        }
      )
    );

    const activeCameraName = createMemo(() => {
      const activeCamIndex = viewModeCapCoord()?.currentCameraIndex() ?? 0;
      const imageCaps = capRegContainer.get(
        CapabilitiesCollection<ImageCapabilityBase>
      )();
      return imageCaps?.capabilities[activeCamIndex]?.cameraName;
    });

    addTilesetPrefetchEffect({
      stationsProvider: capabilityRegistry.get(StationsCapabilityProvider),
      imagingApi,
      viewModeCapCoord,
      tilesetPrefetchContext,
      activeCameraName,
    });

    const stationNavigationController = createMemo(() =>
      viewModeCapCoord()?.stationNavigationController()
    );
    useKeyboardStationNavigation({
      stationNavigationController: stationNavigationController,
      viewMode: viewModeType,
      getStationsNavigationCapability: () =>
        capRegContainer.get(StationsNavigationCapability),
      isHovered,
      keyDownEvent,
    });
  });

  onCleanup(() => {
    // Synchronously remove the container from the registry so reactive
    // consumers stop seeing this pane immediately, then run heavier async
    // teardown in the background.
    capabilityRegistry.removePaneContainer(capRegContainer);
    paneSyncer = undefined;
    unregisterPaneSyncer?.();
    unregisterPaneSyncer = undefined;

    void (async () => {
      try {
        await viewModeCapCoord()?.cleanup();
      } catch (error: unknown) {
        logger.error(
          'ViewModeCapabilityCoordinator cleanup failed',
          undefined,
          asError(error)
        );
      } finally {
        if (viewerWrapper) {
          capabilityRegistry.unregisterPaneContainer(viewerWrapper);
        }
      }
    })();
  });

  function addSyncEffect(viewer: Web3DViewer): void {
    if (!navCapCoord) {
      throw new Error('Navigation capability coordinator is not initialized');
    }

    paneSyncer = new PaneSyncer(
      viewer,
      capRegContainer,
      resolveSyncCoordinator,
      navCapCoord
    );
    unregisterPaneSyncer = props.registerSyncedPane?.(paneSyncer);
  }

  return (
    <div class="relative w-full h-full overflow-hidden group" ref={hoverRef}>
      <div
        class="absolute inset-0 w-full h-full overflow-hidden"
        ref={(x) => {
          placeholder = x;
        }}
      />
      <Show when={emptyViewerStateProps()}>
        <EmptyViewerState
          {...emptyViewerStateProps()!}
          class="absolute inset-0 z-content"
        />
      </Show>

      <Show when={isImageLoading()}>
        <div
          class="pointer-events-none absolute inset-0 z-header flex items-center justify-center"
          data-testid="image-texture-loading-spinner"
        >
          <modus-wc-loader size="lg" variant="spinner" />
        </div>
      </Show>

      <Show when={viewer()}>
        <Show when={isFloatingToolbarVisible()}>
          <ToolbarPositioner anchor={Anchor.TopCenter} autoHide>
            <TrbToolbar orientation="horizontal" addSeparators={false}>
              {/* Camera controls only apply in 3D — there's
                  no camera-mode switching in image / panorama
                  panes — so they're hidden there. The visibility dropdown
                  below stays available in every view mode. */}
              <Show when={viewModeType() === '3D'}>
                <CameraControlSelector
                  alignmentNavigationCapability={
                    capRegContainer.get(AlignmentNavigationCapability)()!
                  }
                  freeViewCapability={
                    capRegContainer.get(FreeViewCapability)()!
                  }
                />
                <Show when={selectionActionsCapability()}>
                  <div
                    data-testid="main-toolbar-show-all-models"
                    class="contents"
                  >
                    <BasicButton
                      iconName="tc-icon-show-all"
                      tooltip={t('web3D.toolbar.actions.showAll')}
                      action={() =>
                        void selectionActionsCapability()?.showAll()
                      }
                      size="small"
                    />
                  </div>
                </Show>
              </Show>
              <PaneToolbarVisibilityControl
                capRegContainer={capRegContainer}
                viewMode={() =>
                  viewModeCapCoord()?.viewMode() ?? { type: '3D' as const }
                }
              />
            </TrbToolbar>
          </ToolbarPositioner>
        </Show>
        <Show when={capRegContainer.get(AlignmentNavigationCapability)()}>
          <ToolbarPositioner anchor={Anchor.TopRight} autoHide>
            <AlignmentNavigationToolbar
              alignmentNavigationCapability={capRegContainer.get(
                AlignmentNavigationCapability
              )()}
              lengthUnit={formattingSettings().lengthUnit as LengthUnit}
              lengthDecimals={formattingSettings().lengthDecimals}
            />
          </ToolbarPositioner>
        </Show>
        <PaneBottomBar
          viewModeCoordinator={viewModeCapCoord}
          viewToolActions={viewToolActions}
          isStationNavVisible={isStationNavBarVisible}
          showStationNavClose={() =>
            viewModeType() !== 'Image' && viewModeType() !== 'Panorama'
          }
          allowPopup={props.allowPopup}
        />
        <Show when={viewModeType() === '3D'}>
          <ToolbarPositioner
            anchor={Anchor.BottomCenter}
            customClasses={isStationNavBarVisible() ? '!bottom-[4.5rem]' : ''}
          >
            <ObjectSelectionToolbarContainer
              capabilityRegistryContainer={capRegContainer}
              customClasses="!p-1"
            />
          </ToolbarPositioner>
        </Show>
      </Show>
    </div>
  );
};
